import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowRight, FiMapPin } from 'react-icons/fi';
import SEO from '../components/SEO';
import { useContent } from '../hooks/useContent';

const FALLBACK = {
  hero_title_line1: 'A Legacy Built on',
  hero_title_highlight: 'Vision and Execution',
  hero_description: 'Founded in Mumbai in 2025, ARVARON emerged with a clear mandate: to consolidate and scale high-potential businesses across foundational sectors of the modern economy.',
  hero_image: 'https://images.unsplash.com/photo-1551135049-8a33b5883817',
  overview_title: 'Built for',
  overview_title_highlight: 'Scale',
  overview_paragraph1: 'We operate as a dynamic holding entity, deploying strategic capital, operational expertise, and technological integration across our portfolio. From global supply chains to premium real estate and digital infrastructure, our approach is defined by rigorous governance, sustainable practices, and an unwavering commitment to value creation.',
  overview_paragraph2: 'Our foundation in Mumbai connects us to one of the world\'s fastest-growing economies, while our vision remains resolutely global. We are building more than a business — we are building an institution.',
  overview_image: 'https://images.unsplash.com/39/lIZrwvbeRuuzqOoWJUEn_Photoaday_CSD%20(1%20of%201)-5.jpg',
  locations: [
    { city: 'Mumbai', country: 'India', role: 'Global Headquarters' },
    { city: 'Riyadh', country: 'Saudi Arabia', role: 'Middle East Hub' },
    { city: 'Dubai', country: 'UAE', role: 'Middle East Operations' },
    { city: 'New York', country: 'USA', role: 'Americas Office' },
    { city: 'Doha', country: 'Qatar', role: 'Middle East' }
  ],
  values: [
    { title: 'Environmental Stewardship', description: 'Implementing green logistics and sustainable sourcing practices to minimize our carbon footprint across all operations.' },
    { title: 'Ethical Governance', description: 'Upholding the highest standards of corporate transparency, accountability, and ethical business conduct.' },
    { title: 'Long-term Value Creation', description: 'Investing in technologies and infrastructure that ensure resilience and sustainable growth for decades to come.' }
  ],
  leadership_message: 'At ARVARON, we are building more than a business — we are building an institution. Our commitment is to create lasting value for our partners, our people, and the nation. Founded in Mumbai with a global vision, every vertical we operate reflects our belief that India\'s best chapter is still ahead of us. We invite you to be part of that story.',
  leadership_attribution: 'The Board of Directors',
  leadership_subtext: 'ARVARON Group of Companies · Mumbai, India · Est. 2025'
};

const About = () => {
  const { content: cms } = useContent('about', FALLBACK);

  return (
    <div className="bg-black text-white" data-testid="about-page">
      <SEO title="About" description="Founded in Mumbai in 2025, Arvaron consolidates and scales high-potential businesses across foundational sectors of the modern economy." url="/about" />
      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={cms.hero_image}
            alt="Corporate boardroom"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            <p className="text-xs uppercase tracking-[0.3em] text-[#17B9BE] mb-6">About Arvaron</p>
            <h1 className="font-display text-5xl md:text-7xl font-bold mb-8 leading-[0.95]" data-testid="about-title">
              {cms.hero_title_line1}<br />
              <span className="brand-gradient-text">{cms.hero_title_highlight}</span>
            </h1>
            <p className="text-lg text-white/50 max-w-3xl mx-auto leading-relaxed">
              {cms.hero_description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Corporate Overview */}
      <section className="py-24 bg-[#0a0a0a]" data-testid="corporate-overview">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src={cms.overview_image}
                alt="Corporate team"
                className="rounded w-full"
              />
              <div className="absolute inset-0 rounded border border-white/5" />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <p className="text-xs uppercase tracking-[0.3em] text-[#17B9BE] mb-4">Corporate Overview</p>
              <h2 className="font-display text-4xl font-bold mb-8">
                {cms.overview_title} <span className="brand-gradient-text">{cms.overview_title_highlight}</span>
              </h2>
              <div className="space-y-5 text-white/50 leading-relaxed">
                <p>{cms.overview_paragraph1}</p>
                <p>{cms.overview_paragraph2}</p>
              </div>
              
              <div className="grid grid-cols-3 gap-8 mt-10 pt-10 border-t border-white/5">
                <div>
                  <div className="text-3xl font-bold brand-gradient-text mb-1">5+</div>
                  <div className="text-xs text-white/40 uppercase tracking-wider">Strategic Verticals</div>
                </div>
                <div>
                  <div className="text-3xl font-bold brand-gradient-text mb-1">15+</div>
                  <div className="text-xs text-white/40 uppercase tracking-wider">Global Markets</div>
                </div>
                <div>
                  <div className="text-3xl font-bold brand-gradient-text mb-1">100%</div>
                  <div className="text-xs text-white/40 uppercase tracking-wider">Committed to Growth</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Global Presence */}
      <section className="py-24 bg-black" data-testid="global-presence">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <p className="text-xs uppercase tracking-[0.3em] text-[#17B9BE] mb-4">Global Presence</p>
            <h2 className="font-display text-4xl md:text-6xl font-bold mb-6">
              Connecting Markets<br />
              <span className="brand-gradient-text">Across Continents</span>
            </h2>
            <p className="text-white/40 text-lg max-w-3xl mx-auto">
              From our headquarters in Mumbai, ARVARON orchestrates operations across key global commercial hubs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(cms.locations || []).map((location, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-[#0a0a0a] p-8 rounded border border-white/5 hover:border-[#17B9BE]/20 transition-all duration-300 gradient-border"
                data-testid={`location-${index}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold mb-1">{location.city}</h3>
                    <p className="text-white/40 text-sm">{location.country}</p>
                  </div>
                  <FiMapPin className="text-[#17B9BE] text-lg" />
                </div>
                <p className="text-xs text-white/30 uppercase tracking-wider">{location.role}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sustainability & Governance */}
      <section className="py-24 bg-[#0a0a0a]" data-testid="sustainability-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <p className="text-xs uppercase tracking-[0.3em] text-[#17B9BE] mb-4">Sustainability & Governance</p>
            <h2 className="font-display text-4xl md:text-6xl font-bold mb-6">
              Building for the<br />
              <span className="brand-gradient-text">Next Generation</span>
            </h2>
            <p className="text-white/40 text-lg max-w-3xl mx-auto">
              At ARVARON, we recognize that true industrial leadership requires a profound commitment to sustainable practices and rigorous governance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(cms.values || []).map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-black p-10 rounded border border-white/5 hover:border-[#17B9BE]/20 transition-all duration-300"
                data-testid={`value-${index}`}
              >
                <div className="w-12 h-12 rounded-sm brand-gradient-bg flex items-center justify-center text-black font-bold text-lg mb-6">
                  {index + 1}
                </div>
                <h3 className="text-xl font-bold mb-4">{value.title}</h3>
                <p className="text-white/40 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Message */}
      <section className="py-24 bg-black relative" data-testid="leadership-section">
        <div className="absolute inset-0 hero-overlay" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <p className="text-xs uppercase tracking-[0.3em] text-[#17B9BE] mb-6">Leadership</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold mb-10">Message from the Board</h2>
            
            <div className="relative">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-4 text-6xl text-[#17B9BE]/20 font-display">"</div>
              <blockquote className="text-xl md:text-2xl text-white/60 italic mb-10 leading-relaxed px-8">
                {cms.leadership_message}
              </blockquote>
            </div>

            <hr className="brand-hr w-24 mx-auto mb-6 opacity-50" />
            <p className="text-white/70 font-semibold">{cms.leadership_attribution}</p>
            <p className="text-xs text-white/30 mt-2 uppercase tracking-wider">{cms.leadership_subtext}</p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-4xl font-bold mb-6">
              <span className="brand-gradient-text">Learn Our Story</span>
            </h2>
            <p className="text-white/40 text-lg mb-10 max-w-2xl mx-auto">
              Discover how we're building tomorrow's global economy
            </p>
            <Link
              to="/contact"
              data-testid="about-cta-button"
              className="group px-10 py-4 brand-gradient-bg text-black font-semibold rounded-sm hover:opacity-90 transition-all inline-flex items-center justify-center text-sm uppercase tracking-wider"
            >
              Connect with Us
              <FiArrowRight className="ml-3 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
